export type CustomerBenefitCursor = {
  changed_at: string | null;
  entity_id: string | null;
};

export type CustomerBenefitSourceType = "LOYALTY_REWARD" | "PROMOTION_CLAIM";

export type CustomerBenefitRuntimeItem = {
  projection_kind: "CUSTOMER_BENEFIT";
  projection_id: string;
  owner_domain: "LOYALTY_CORE" | "PROMOTION_CORE" | string;
  source_type: CustomerBenefitSourceType | string;
  source_instance_id: string;
  customer_id: string;
  state: string;
  active: boolean;
  reward_version_id?: string | null;
  point_cost_snapshot?: number | null;
  promotion_version_id?: string | null;
  claim_definition_id?: string | null;
  benefit_snapshot: Record<string, unknown>;
  policy_snapshot: Record<string, unknown>;
  branch_id?: string | null;
  commerce_channel_id?: string | null;
  source_order_id?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  changed_at: string;
};

export type CustomerBenefitProjectionPayload = {
  ok: boolean;
  contract: "GET_POS_CUSTOMER_BENEFIT_RUNTIME_V1" | string;
  owner: string;
  organization_id: string;
  branch_id: string;
  mode: "FULL" | "DELTA" | "DISABLED";
  items: CustomerBenefitRuntimeItem[];
  next_cursor: CustomerBenefitCursor | null;
  has_more: boolean;
  server_at: string;
};

type Envelope = { ok?: boolean; data?: CustomerBenefitProjectionPayload; error?: string };

export class PosCustomerBenefitGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  async load(input: {
    deviceSecret: string;
    sessionToken: string;
    cursor: CustomerBenefitCursor | null;
    limit?: number;
  }): Promise<{ ok: boolean; data: CustomerBenefitProjectionPayload | null; error: string | null }> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/local-projections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          domain: "CUSTOMER_BENEFIT",
          cursor: input.cursor,
          limit: input.limit ?? 500,
        }),
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
      });
      const payload = (await response.json().catch(() => null)) as Envelope | null;
      if (!response.ok || !payload?.ok || !payload.data) {
        return { ok: false, data: null, error: payload?.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError"
          ? "CUSTOMER_BENEFIT_SYNC_TIMEOUT"
          : "CUSTOMER_BENEFIT_SYNC_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
